<template>
  <div>
    <div v-if="!loading">
      <template v-if="files.length !== 0">
        <div class="p-box">
          <form class="p-box__header">
            <div class="p-filtersA">
              <div class="p-filtersA__left">
                <div class="c-buttonFilter" @click="openModal">
                  {{ $t('pages.CompaniesSurveys.filters') }}
                  <div class="c-buttonFilter__icon">
                    <img :src="iconFilterPath">
                  </div>
                </div>
              </div>
              <div class="p-filtersA__right">
                <form class="c-searchField" onsubmit="return false;">
                  <input
                    v-model="searchQuery"
                    :disabled="loading"
                    type="text"
                    class="searchForm"
                    :placeholder="$t('labels.search')"
                    @keydown.enter="search"
                  >
                  <Icon name="iconSearch" />
                </form>
                <div class="c-select">
                  <DatePicker
                    v-if="period"
                    :initial-dates="period"
                    opens="left"
                    @input="changePeriod"
                  />
                </div>
                <KkButton
                  v-if="showOnlyOwnerAuthority"
                  class="csvButton"
                  :to="{ name: 'CompaniesTestimonialFiles', params: { company_uuid: currentCompanyUuid }}"
                  color="secondary"
                  height="38px"
                  :font-bold="false"
                  :text="$t('pages.CompaniesTestimonials.file.title')"
                />
              </div>
            </div>
          </form>
          <div class="p-box__body">
            <div class="c-info">
              <div class="c-info__icon">
                <Icon name="iconQuestion" />
              </div>
              <div class="c-info__content">
                <i18n-t keypath="pages.CompaniesTestimonialIndex.description" tag="div">
                  <template #keyword_link>
                    <a :href="`/s/companies/${currentCompanyUuid}/analysis/keywords`">
                      {{ $t(`pages.CompaniesTestimonialFiles.description.keyword_label`) }}
                    </a>
                  </template>
                </i18n-t>
              </div>
            </div>
            <div class="totalCount">
              {{ $t('pages.CompaniesTestimonials.totalCount', { value: numberLocaleString(meta.totalCount) }) }}
            </div>
            <TestimonialsTable
              :datas="texts"
              :sort-key="sortKey"
              :sort-order="sortOrder"
              @sort="sort"
            />
            <div v-if="texts.length !== 0" class="p-controller">
              <div class="p-controller__count">
                <p class="p-controller__count__name">
                  {{ $t('labels.perPageCount') }}
                </p>
                <div class="p-controller__count__select">
                  <select :value="displayNumber" @change="changeDisplayNumber($event)">
                    <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                  </select>
                </div>
                <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
              </div>
              <div class="p-controller__navi">
                <button
                  type="button"
                  :disabled="loading || currentPage === 1"
                  class="p-controller__navi__prev"
                  @click="goPrevPage"
                >
                  <Icon name="arrowLeft" />
                </button>
                <button
                  type="button"
                  :disabled="loading || currentPage >= meta.totalPages"
                  class="p-controller__navi__next"
                  @click="goNextPage"
                >
                  <Icon name="arrowRight" />
                </button>
              </div>
            </div>
          </div>
          <KkFilterModal
            :is-visible="modal"
            :filter-items="filterItems"
            :form-values="formValues"
            :total-count="filteredCount"
            @close="modal = false"
            @submit="handleResetPageAndSubmit"
            @reset="handleReset"
            @change-check-box="handleChangeFilterModalCheckBox"
          />
        </div>
      </template>
      <template v-else>
        <KkTutorial
          :description1="$t('pages.CompaniesTestimonials.file.noFile.description1')"
          :description2="$t('pages.CompaniesTestimonials.file.noFile.description2')"
          :img-path="tutorialImgPath"
          :tutorial-description="$t('pages.CompaniesTestimonialIndex.tutorial.description').replace(/\n/g,'<br/>')"
          class="noFile"
        >
          <template #buttonArea>
            <div class="noFile__button--white" @click="clickDownloadTemplate">
              {{ $t('pages.CompaniesTestimonials.file.templateDownload') }}
            </div>
            <label for="fileUpload" class="noFile__button--red">
              {{ $t('pages.CompaniesTestimonials.file.csvUpload') }}
              <input id="fileUpload" type="file" name="file" @change="selectedFile">
            </label>
          </template>
        </KkTutorial>
      </template>
    </div>
    <Loading v-show="loading" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import Loading from '../../../components/Loading'
import TestimonialsTable from '../../../components/CompaniesTestimonials/Table.vue'
import DatePicker from '../../../components/DatePicker'
import { KkFilterModal, KkTutorial } from '../../../components/projects'
import { KkButton } from '../../../components/base'
import Icon from '../../../components/Icon.vue'
import iconFilterPath from '../../../../../images/common/icon_filter.svg'
import { toNumberLocaleString } from '../../../modules/localeString'
import { i18n } from '../../../plugins/i18n'
import moment from 'moment'
import tutorialImgPath from '../../../../../images/testimonials/tutorial.png'
import { addAttributes } from '../../../modules/filterModalConst'

export default {
  name: 'CompaniesTestimonialIndex',
  components: {
    Loading,
    TestimonialsTable,
    KkFilterModal,
    KkTutorial,
    DatePicker,
    Icon,
    KkButton
  },
  beforeRouteUpdate (_to, _from, next) {
    next()
    this.handleResetPageAndSubmit()
  },
  data() {
    return {
      modal: false,
      currentPage: 1,
      sortKey: 'written_at',
      sortOrder: 'desc',
      searchQuery: this.$route.query['query'] || '',
      iconFilterPath,
      filterItems: [],
    }
  },
  computed: {
    ...mapState({
      texts: state => state.companiesTestimonials.data,
      meta: state => state.companiesTestimonials.meta,
      files: state => state.companiesTestimonialsFiles.data,
      companiesTestimonialsLoading: state => state.companiesTestimonials.loading,
      companiesTestimonialsFilesLoading: state => state.companiesTestimonialsFiles.loading,
      period: state => state.filters.period,
      formValues: state => state.companiesTestimonials.formValues,
      filteredCount: state => state.companiesTestimonials.filteredCount,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      displayNumber: state => state.filters.displayNumber,
      tutorialImgPath: () => tutorialImgPath
    }),
    ...mapGetters({
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
    }),
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    loading(){
      return this.companiesTestimonialsLoading || this.searchAttributesLoading || this.companiesTestimonialsFilesLoading
    },
    query() {
      return {
        biz_companies_testimonials_form: {
          store_uuids: this.formValues.stores,
          brand_ids: this.formValues.brands,
          franchisee_ids: this.formValues.franchisees,
          user_company_ids: this.formValues.user_companies,
          area_ids: this.formValues.areas,
          prefectures: this.formValues.prefectures,
          original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
          original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
          original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
          original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
          original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
          per: this.displayNumber,
          page: this.currentPage,
          sort_key: this.sortKey,
          sort_order: this.sortOrder,
          query: this.searchQuery,
          term: {
            start_date: moment(this.period.startDate).format(i18n.t('format.date.default')),
            end_date: moment(this.period.endDate).format(i18n.t('format.date.default'))
          },
        }
      }
    },
    startDataNumber: function() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber: function() {
      return this.startDataNumber + this.texts.length - 1
    },
  },
  async created() {
    await this.readSearchAttributes(this.currentCompanyUuid)
    this.setFormValues(this.formValuesFromQueryParams())
    this.createFilter()
    this.readTexts({company_uuid: this.currentCompanyUuid, params: this.query})
    this.readFiles({
      company_uuid: this.currentCompanyUuid,
      query: {
        page: 1,
        sort_order: 'desc',
      }
    })
  },
  methods: {
    ...mapActions({
      readTexts: 'companiesTestimonials/readTexts',
      readTextsForFilter: 'companiesTestimonials/readTextsForFilter',
      readFiles: 'companiesTestimonialsFiles/readFiles',
      setPeriod: 'filters/setPeriod',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      setDisplayNumber: 'filters/setDisplayNumber',
      uploadFile: 'companiesTestimonialsFiles/uploadFile',
      downloadTemplate: 'companiesTestimonialsFiles/downloadTemplate',
    }),
    ...mapMutations({
      setFormValues: 'companiesTestimonials/setFormValues',
    }),
    clickDownloadTemplate() {
      this.downloadTemplate(this.currentCompanyUuid)
    },
    async selectedFile(e) {
      e.preventDefault()
      const data = new FormData()
      data.append('testimonial_file[file]', e.target.files[0])
      const isSuccess = await this.uploadFile({company_uuid: this.currentCompanyUuid, data: data})
      if(isSuccess) this.$router.push({ name: 'CompaniesTestimonialFiles', params: { company_uuid: this.currentCompanyUuid }})
    },
    handleSubmit() {
      this.readTexts({company_uuid: this.currentCompanyUuid, params: this.query})
      this.closeModal()
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    /**
     * 絞り込み条件のリセット
     * @return { Void }
     */
    handleReset() {
      this.setFormValues(this.formInitialValues())
      this.handleSubmit()
      this.modal = false
    },
    /**
     * 検索キーワードの変更
     * @param  { Object } event
     * @return { Void }
     */
    search(event) {
      if (event.keyCode !== 13) return
      if (this.searchQuery === (this.$route.query['query'] || '')) return
      const query = { 'query': this.searchQuery || undefined }
      this.$router.push({ query })
    },
    sort(key) {
      if (this.loading) return
      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
      this.handleResetPageAndSubmit()
    },
    openModal() {
      if (this.loading) return
      this.modal = true
    },
    closeModal() {
      this.modal = false
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 検索条件の初期値
     * @return { Object }
     */
    formInitialValues() {
      return {
        stores: [],
        brands: [],
        franchisees: [],
        areas: [],
        user_companies: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: [],
      }
    },
    formValuesFromQueryParams() {
      /**
       * $route.query['xxxxx'] の値は string or array or undefined のいずれか
       * ここで array に変換する
       */
      const {
        stores,
        brands,
        franchisees,
        areas,
        user_companies: userCompanies,
        prefectures,
        original_tag_group_a: originalTagGroupA,
        original_tag_group_b: originalTagGroupB,
        original_tag_group_c: originalTagGroupC,
        original_tag_group_d: originalTagGroupD,
        original_tag_group_e: originalTagGroupE,
      } = this.$route.query

      const storesConvertToArray = []
      if (typeof stores === 'string') {
        storesConvertToArray.push(stores.split(','))
      } else if (Array.isArray(stores)) {
        storesConvertToArray.push(...stores)
      }

      const brandsConvertToArray = []
      if (typeof brands === 'string') {
        brandsConvertToArray.push(brands.split(',').map(Number))
      } else if (Array.isArray(brands)) {
        brandsConvertToArray.push(...brands.map(Number))
      }

      const franchiseesConvertToArray = []
      if (typeof franchisees === 'string') {
        franchiseesConvertToArray.push(franchisees.split(',').map(Number))
      } else if (Array.isArray(franchisees)) {
        franchiseesConvertToArray.push(...franchisees.map(Number))
      }

      const areasConvertToArray = []
      if (typeof areas === 'string') {
        areasConvertToArray.push(areas.split(',').map(Number))
      } else if (Array.isArray(areas)) {
        areasConvertToArray.push(...areas.map(Number))
      }

      const userCompaniesConvertToArray = []
      if (typeof areas === 'string') {
        userCompaniesConvertToArray.push(userCompanies.split(',').map(Number))
      } else if (Array.isArray(userCompanies)) {
        userCompaniesConvertToArray.push(...userCompanies.map(Number))
      }

      const prefecturesConvertToArray = []
      if (typeof areas === 'string') {
        prefecturesConvertToArray.push(prefectures.split(',').map(Number))
      } else if (Array.isArray(prefectures)) {
        prefecturesConvertToArray.push(...prefectures.map(Number))
      }

      const originalTagGroupAConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupAConvertToArray.push(originalTagGroupA.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupA)) {
        originalTagGroupAConvertToArray.push(...originalTagGroupA.map(Number))
      }

      const originalTagGroupBConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupBConvertToArray.push(originalTagGroupB.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupB)) {
        originalTagGroupBConvertToArray.push(...originalTagGroupB.map(Number))
      }

      const originalTagGroupCConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupCConvertToArray.push(originalTagGroupC.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupC)) {
        originalTagGroupCConvertToArray.push(...originalTagGroupC.map(Number))
      }

      const originalTagGroupDConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupDConvertToArray.push(originalTagGroupD.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupD)) {
        originalTagGroupDConvertToArray.push(...originalTagGroupD.map(Number))
      }

      const originalTagGroupEConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupEConvertToArray.push(originalTagGroupE.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupE)) {
        originalTagGroupEConvertToArray.push(...originalTagGroupE.map(Number))
      }

      return {
        stores: storesConvertToArray,
        brands: brandsConvertToArray,
        franchisees: franchiseesConvertToArray,
        areas: areasConvertToArray,
        user_companies: userCompaniesConvertToArray,
        prefectures: prefecturesConvertToArray,
        original_tag_group_a: originalTagGroupAConvertToArray,
        original_tag_group_b: originalTagGroupBConvertToArray,
        original_tag_group_c: originalTagGroupCConvertToArray,
        original_tag_group_d: originalTagGroupDConvertToArray,
        original_tag_group_e: originalTagGroupEConvertToArray,
      }
    },
    initData() {
      this.setFormValues(this.formInitialValues())
      this.searchQuery = ''
      this.sortKey = 'written_at'
      this.sortOrder = 'desc'
    },
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)

      this.readTextsForFilter({company_uuid: this.currentCompanyUuid, params: this.query})
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ]

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  },
}
</script>

<style lang="scss" scoped>
.noFile {
  input {
    display: none;
  }
  %__button {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 14px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 14px;
    line-height: 1;
    text-decoration: none;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
    transition: .2s;
  }
  &__button {
    &--white {
      @extend %__button;
      border: 1px solid #E4E7EB;
      background: #FFF;
    }
    &--red {
      @extend %__button;
      background: #E9446A;
      color: #FFF;
    }
  }
}
.totalCount {
  margin-bottom: 10px;
  font-weight: bold;
}
.questionBody__topSection {
  display: flex;
  align-items: center;
}
.questionDescription {
  margin-left: 30px;
}
.csvButton {
  margin-left: 10px;
}
</style>
